import React from "react";
import { Box, Button, Flex, Text, useTranslation, Checkbox } from "@familyzone/component-library";
import { Stack } from "@chakra-ui/react";
import { DomainsMultiSelectInput } from "../../shared/DomainsMultiSelectInput";
import { SectionHeading } from "../../shared/SectionHeading";
import { TabTypes } from ".";

interface GeneralSettingsTabProps {
  saving: boolean;
  disabled: boolean;
  onSave: (tabType: TabTypes) => void;
  excludedDomains: string[];
  setExcludedDomains: (domains: string[]) => void;
  guiltByAssociationEnabled: boolean;
  setGuiltByAssociationEnabled: (val: boolean) => void;
}

const GeneralSettingsTab = ({
  saving,
  disabled,
  onSave,
  excludedDomains,
  setExcludedDomains,
  guiltByAssociationEnabled,
  setGuiltByAssociationEnabled,
}: GeneralSettingsTabProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" minH="100%">
      <Box backgroundColor="white" borderRadius="2xl" boxShadow="sm" pt="sp24">
        <Stack spacing="sp8">
          <Box px="sp24">
            <SectionHeading title={t("Excluded domains")} aria-level={2} />
            <Text color="neutrals.120" mt="sp8" ml="sp12" mr="sp12">
              {t("Any domains/subdomains added here will be excluded from content aware filtering.")}
            </Text>
          </Box>

          <Box px="sp24">
            <Text color="neutrals.120" mt="sp16" ml="sp12" mr="sp12">
              {t("Excluded Domains/ Subdomains (Max 50 entries)")}
            </Text>
          </Box>

          <Box px="sp24">
            <Box ml="sp12" mr="sp12">
              <DomainsMultiSelectInput
                placeholder={t("Paste Domains / Subdomain URLs")}
                domains={excludedDomains}
                onDomainsChanged={setExcludedDomains}
                disabled={disabled}
                aria-label={t("Excluded Domains/ Subdomains (Max 50 entries)")}
              />
            </Box>
          </Box>

          <Box px="sp24">
            <Text color="text.paragraph.regular" pb="sp8" ml="sp12" mr="sp12">
              {t(
                "You can exempt individual domains (example.com) and subdomains (test.example.com) or use the * wildcard to exempt a domain and all its subdomains (*.example.com)."
              )}
            </Text>
          </Box>
        </Stack>

        <Box mt="sp36">
          <Box px="sp24" mt="sp24">
            <SectionHeading title={t("Guilt by association")} aria-level={2} />
            <Text color="neutrals.120" mt="sp8" ml="sp12" mr="sp12">
              {t(
                "Automatically blocks all visual content on a webpage once a set number of images or videos are identified as belonging to a blocked Content Aware category."
              )}
            </Text>

            <Box mt="sp24" ml="sp12" color="neutrals.120">
              <Checkbox
                isChecked={guiltByAssociationEnabled}
                isDisabled={disabled}
                aria-label={`Turn on Guilt by Association ${guiltByAssociationEnabled ? "enabled" : "disabled"}`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGuiltByAssociationEnabled(e.target.checked)}
              >
                <Text fontWeight="medium">{t("Turn on Guilt by Association")}</Text>
              </Checkbox>
            </Box>
          </Box>
        </Box>

        <Box bg="#F5FCFF" mt="sp24" px="sp24" py="sp24" borderBottomLeftRadius="2xl" borderBottomRightRadius="2xl">
          <Flex>
            <Button
              variant="primary"
              disabled={disabled}
              onClick={() => onSave(TabTypes.GENERAL)}
              data-testid="save-content-aware-config-button"
              aria-label={saving ? t("Saving...") : t("Save Changes")}
            >
              {saving ? (
                <div style={{ width: "90px", margin: "1px 0" }}>{t("Saving...")}</div>
              ) : (
                <div style={{ width: "90px", margin: "1px 0" }}>{t("Save Changes")}</div>
              )}
            </Button>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

export default GeneralSettingsTab;
